/**
 * Project hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectService } from '@/api'
import { useUIStore } from '@/stores/uiStore'
import { handleApiError } from '@/lib/apiErrorHandler'
import type {
  ProjectCreate,
  ProjectUpdate,
  ProjectFilterParams,
  ProjectTransition,
  ProjectResponse,
  ProjectListResponse,
} from '@/types/api'

/**
 * Query keys for projects
 */
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters: ProjectFilterParams) => [...projectKeys.lists(), filters] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  byLead: (leadId: string) => [...projectKeys.all, 'lead', leadId] as const,
}

/**
 * Hook to get paginated projects
 */
export function useProjects(params?: ProjectFilterParams) {
  return useQuery<ProjectListResponse>({
    queryKey: projectKeys.list(params || {}),
    queryFn: () => projectService.getProjects(params),
  })
}

/**
 * Hook to get a single project
 */
export function useProject(id: string) {
  return useQuery<ProjectResponse>({
    queryKey: projectKeys.detail(id),
    queryFn: () => projectService.getProject(id),
    enabled: !!id,
  })
}

/**
 * Hook to get all projects for a lead
 */
export function useLeadProjects(leadId: string) {
  return useQuery<ProjectListResponse>({
    queryKey: projectKeys.byLead(leadId),
    queryFn: () => projectService.getLeadProjects(leadId),
    enabled: !!leadId,
  })
}

/**
 * Hook for creating a project
 */
export function useCreateProject() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: ProjectCreate) => projectService.createProject(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectKeys.byLead(data.lead_id) })
      addToast({
        title: 'Project created',
        description: 'New project has been added',
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to create project')
    },
  })
}

/**
 * Hook for updating a project
 */
export function useUpdateProject(id: string) {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: ProjectUpdate) => projectService.updateProject(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(projectKeys.detail(id), data)
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectKeys.byLead(data.lead_id) })
      addToast({
        title: 'Project updated',
        description: 'Changes have been saved',
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to update project')
    },
  })
}

/**
 * Hook for deleting a project
 */
export function useDeleteProject() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (id: string) => projectService.deleteProject(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: projectKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectKeys.all })
      addToast({
        title: 'Project deleted',
        description: 'Project deleted successfully',
        variant: 'delete_success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to delete project')
    },
  })
}

/**
 * Hook for transitioning project status
 */
export function useTransitionProject(id: string) {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: ProjectTransition) => projectService.transitionProject(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(projectKeys.detail(id), data)
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
      queryClient.invalidateQueries({ queryKey: projectKeys.byLead(data.lead_id) })
      addToast({
        title: 'Status updated',
        description: `Project moved to ${data.project_status.replace('_', ' ')}`,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to update project status')
    },
  })
}
